import {
	Box,
	Flex,
	Icon,
	SimpleGrid,
	Skeleton,
	Text,
	VStack,
	useColorModeValue,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
	MdSportsTennis,
	MdEvent,
	MdPeople,
	MdBarChart,
	MdAdd,
	MdCalendarMonth,
	MdCardMembership,
	MdLocalOffer,
} from 'react-icons/md';
import Card from '../../../components/card/Card';
import { spacing, typography, useSemanticColor } from '../../../theme/tokens';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useSelectedLocation } from '../../location-management/hooks/useSelectedLocation';
import { useGetRevenue } from '../../analytics/hooks/useGetRevenue';
import { useGetGameCount } from '../../analytics/hooks/useGetGameCount';
import { useGetAllCourtsUsage } from '../../analytics/hooks/useGetAllCourtsUsage';
import { useGetAdminNotifications } from '../../admin-notifications/hooks/useGetAdminNotifications';
import { AdminNotificationType } from '../../../models/admin-notification/dto/admin-notification-feed.dto';
import { useGetCourts } from '../../court-management/hooks/useGetCourts';
import { isDemoOrganization, getDemoRevenue, getDemoGameCount, getDemoCourtsUsageRatio, getDemoCumulativeBookings, getDemoCumulativeRevenue } from '../../analytics/demoAnalyticsData';
import { computeGrowth } from '../../analytics/utils/computeGrowth';
import { UNKNOWN_STAT_VALUE, isUnreadable } from '../../analytics/utils/statValue';
import {
	READ_FAILED_I18N_KEY,
	scopePromptI18nKey,
	classifyRead,
} from '../../_shared/readState';
import { useGetCumulativeBookings } from '../../analytics/hooks/useGetCumulativeBookings';
import { useGetCumulativeRevenue } from '../../analytics/hooks/useGetCumulativeRevenue';
import CumulativeBookingsChart from '../../analytics/components/CumulativeBookingsChart';
import CumulativeRevenueChart from '../../analytics/components/CumulativeRevenueChart';
import Sparkline from '../../../components/card/Sparkline';

// ---------------------------------------------------------------------------
// Display font for the date heading — only loaded when Clubhouse aesthetic is active
// ---------------------------------------------------------------------------
const displayFont = '"Instrument Serif", "Urbanist", serif';
const monoFont = '"JetBrains Mono", "Fira Code", monospace';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDateDisplay(): string {
	return new Date().toLocaleDateString(undefined, {
		weekday: 'long',
		month: 'long',
		day: 'numeric',
	});
}

function formatTime(date: Date | string): string {
	const d = typeof date === 'string' ? new Date(date) : date;
	return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatCurrency(value: number): string {
	return `€${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// ---------------------------------------------------------------------------
// Score Item — the big scoreboard numbers
// ---------------------------------------------------------------------------
function ScoreItem({
	label,
	value,
	unit,
	change,
	changeDirection,
	isLoading,
	isError,
	sparklineData,
}: {
	label: string;
	value: string;
	unit?: string;
	change?: string;
	changeDirection?: 'up' | 'down';
	isLoading?: boolean;
	/**
	 * The read behind this number failed and there is nothing to show. The
	 * scoreboard then prints `UNKNOWN_STAT_VALUE` with no unit, no change chip
	 * and no sparkline, rather than a number nobody measured.
	 */
	isError?: boolean;
	sparklineData?: number[];
}) {
	const mutedColor = useSemanticColor('textMuted');
	const successBg = useColorModeValue('green.100', 'rgba(16, 185, 129, 0.12)');
	const successColor = useColorModeValue('green.500', '#6EE7B7');
	const dangerBg = useColorModeValue('danger.50', 'rgba(184, 69, 58, 0.12)');
	const dangerColor = useColorModeValue('danger.500', '#FCA5A5');

	return (
		<Box>
			<Text
				fontFamily='"Urbanist", sans-serif'
				fontSize="11px"
				fontWeight="700"
				textTransform="uppercase"
				letterSpacing="0.06em"
				color={mutedColor}
				mb="4px"
			>
				{label}
			</Text>
			{isLoading ? (
				<Skeleton height="48px" width="120px" borderRadius="8px" />
			) : isError ? (
				<Text
					fontFamily='"Urbanist", sans-serif'
					fontSize={{ base: '36px', md: '42px' }}
					fontWeight="800"
					letterSpacing="-0.03em"
					lineHeight="1"
					color={dangerColor}
					role="status"
				>
					{UNKNOWN_STAT_VALUE}
				</Text>
			) : (
				<Text
					fontFamily='"Urbanist", sans-serif'
					fontSize={{ base: '36px', md: '42px' }}
					fontWeight="800"
					letterSpacing="-0.03em"
					lineHeight="1"
					sx={{ fontFeatureSettings: '"tnum" 1' }}
				>
					{value}
					{unit && (
						<Text
							as="span"
							fontSize={{ base: '20px', md: '24px' }}
							fontWeight="600"
							color={mutedColor}
						>
							{unit}
						</Text>
					)}
				</Text>
			)}
			{!isError && change && (
				<Text
					fontFamily={monoFont}
					fontSize="12px"
					fontWeight="500"
					mt="6px"
					display="inline-flex"
					alignItems="center"
					gap="4px"
					px="8px"
					py="2px"
					borderRadius="6px"
					bg={changeDirection === 'up' ? successBg : dangerBg}
					color={changeDirection === 'up' ? successColor : dangerColor}
				>
					{change}
				</Text>
			)}
			{!isError && sparklineData && sparklineData.length >= 2 && (
				<Box mt="8px">
					<Sparkline data={sparklineData} width={100} height={28} />
				</Box>
			)}
		</Box>
	);
}

// ---------------------------------------------------------------------------
// Activity Feed
// ---------------------------------------------------------------------------
// Exported for `_shared/navigation-read-state.test.tsx`: this panel's three
// non-answer states are the point of run 101, and driving them through the
// whole ClubhouseDashboard would mock eight unrelated hooks to reach one Card.
export function ActivityFeed({
	organizationId,
}: {
	organizationId: string | undefined;
}) {
	const navigate = useNavigate();
	const mutedColor = useSemanticColor('textMuted');
	const textSecondary = useSemanticColor('textSecondary');
	const textPrimary = useSemanticColor('textPrimary');
	const borderSubtle = useColorModeValue('secondaryGray.400', 'whiteAlpha.50');
	const hoverBg = useColorModeValue('secondaryGray.400', 'whiteAlpha.50');

	// Dot color per notification type
	const dotColorMap: Record<string, string> = {
		[AdminNotificationType.courtBookingCreated]: useColorModeValue('brand.400', 'brand.300'),
		[AdminNotificationType.courtBookingCanceled]: useColorModeValue('danger.500', 'danger.500'),
		[AdminNotificationType.paymentFailed]: useColorModeValue('warning.500', 'warning.500'),
	};
	const defaultDot = useColorModeValue('green.500', '#6EE7B7');

	const { data, isInitialLoading, isError } = useGetAdminNotifications({
		organizationId,
		page: 0,
		limit: 8,
	});
	// `organizationId` is `selectedOrganization?.id`, and the read is gated on
	// it. With nothing selected the query is disabled, which React Query v4
	// reports as `isLoading: true` — so before run 101 the operator's HOME
	// SCREEN showed four skeletons that never resolved, and the moment that was
	// corrected the panel would instead have claimed "No notifications yet"
	// about a feed it never asked for.
	const readState = classifyRead(isError, isInitialLoading, data);

	if (readState === 'loading') {
		return (
			<Card>
				<Text {...typography.sectionTitle} mb={4}>
					{translate('admin_notifications.title')}
				</Text>
				{Array.from({ length: 4 }).map((_, i) => (
					<Skeleton key={i} height="20px" mb={3} borderRadius="4px" />
				))}
			</Card>
		);
	}

	if (readState === 'unreadable' || readState === 'never-issued') {
		return (
			<Card>
				<Text {...typography.sectionTitle} mb={4}>
					{translate('admin_notifications.title')}
				</Text>
				<Text
					fontSize="13px"
					color={mutedColor}
					py={4}
					textAlign="center"
					data-testid={`activity-feed-${readState}`}
				>
					{translate(
						(readState === 'unreadable'
							? READ_FAILED_I18N_KEY
							: scopePromptI18nKey('organization')) as any,
					)}
				</Text>
			</Card>
		);
	}

	const notifications = data?.notifications ?? [];

	return (
		<Card>
			<Flex justify="space-between" align="center" mb={4}>
				<Text {...typography.sectionTitle}>
					{translate('admin_notifications.title')}
				</Text>
				{notifications.length > 0 && data?.unreadCount != null && data.unreadCount > 0 && (
					<Text fontSize="12px" fontWeight="600" color="brand.500">
						{data.unreadCount} {translate('admin_notifications.unread')}
					</Text>
				)}
			</Flex>

			{notifications.length === 0 ? (
				<Text fontSize="13px" color={mutedColor} py={4} textAlign="center">
					{translate('admin_notifications.empty')}
				</Text>
			) : (
				notifications.map((notif, i) => {
					const gameId = notif.data?.gameId;
					const isClickable = Boolean(gameId);
					return (
					<Flex
						key={notif.id}
						gap={4}
						py="10px"
						px="8px"
						mx="-8px"
						borderBottom={i < notifications.length - 1 ? '1px solid' : 'none'}
						borderColor={borderSubtle}
						align="baseline"
						opacity={notif.isRead ? 0.7 : 1}
						cursor={isClickable ? 'pointer' : undefined}
						borderRadius="8px"
						transition="background 0.12s ease"
						_hover={isClickable ? { bg: hoverBg } : undefined}
						onClick={isClickable ? () => navigate(`/admin/gameDetails?game=${gameId}`) : undefined}
					>
						<Text
							fontFamily={monoFont}
							fontSize="12px"
							color={mutedColor}
							flexShrink={0}
							w="40px"
						>
							{formatTime(notif.createdAt)}
						</Text>
						<Box
							w="6px"
							h="6px"
							borderRadius="50%"
							flexShrink={0}
							mt="6px"
							bg={dotColorMap[notif.type] ?? defaultDot}
						/>
						<Text fontSize="13px" color={textSecondary} lineHeight="1.4">
							<Text as="span" fontWeight={notif.isRead ? '500' : '600'} color={textPrimary}>
								{notif.title}
							</Text>
							{' — '}
							{notif.message}
						</Text>
					</Flex>
					);
				})
			)}
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Quick Actions
// ---------------------------------------------------------------------------
const QUICK_ACTIONS = [
	{ icon: MdSportsTennis, label: 'tabs.courts', route: '/admin/courts', color: 'brand.500', iconBg: 'brand.50' },
	{ icon: MdEvent, label: 'tabs.events', route: '/admin/events', color: 'success.500', iconBg: 'success.50' },
	{ icon: MdPeople, label: 'tabs.users', route: '/admin/users', color: 'info.500', iconBg: 'info.50' },
	{ icon: MdBarChart, label: 'tabs.analytics', route: '/admin/analytics', color: 'warning.500', iconBg: 'warning.50' },
	{ icon: MdCalendarMonth, label: 'tabs.calendar', route: '/admin/calendar', color: 'accent.600', iconBg: 'accent.50' },
	{ icon: MdCardMembership, label: 'tabs.memberships', route: '/admin/memberships', color: 'brand.400', iconBg: 'brand.50' },
	{ icon: MdLocalOffer, label: 'coupon.coupons', route: '/admin/coupons', color: 'danger.500', iconBg: 'danger.50' },
	{ icon: MdAdd, label: 'tabs.games', route: '/admin/latest-booked-games', color: 'navy.600', iconBg: 'secondaryGray.300' },
];

// ---------------------------------------------------------------------------
// Main Clubhouse Dashboard
// ---------------------------------------------------------------------------
export default function ClubhouseDashboard() {
	const selectedOrganization = useSelectedOrganization.use.selectedOrganization();
	const { selectedLocation } = useSelectedLocation();
	const mutedColor = useSemanticColor('textMuted');
	const textColor = useSemanticColor('textPrimary');
	const actionHoverBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
	const actionBorderColor = useColorModeValue('rgba(0, 0, 0, 0.04)', 'rgba(255, 255, 255, 0.06)');

	const hasFullContext = Boolean(selectedOrganization && selectedLocation);
	const isDemo = isDemoOrganization(selectedOrganization?.id);
	const orgId = selectedOrganization?.id;

	// Date ranges
	const { startDate, endDate, previousStartDate, previousEndDate } = useMemo(() => {
		const now = new Date();
		const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
		const end = new Date(now);
		end.setHours(23, 59, 59, 999);
		const start = new Date(now.getTime() - thirtyDaysMs);
		const prevEnd = new Date(start.getTime());
		const prevStart = new Date(start.getTime() - thirtyDaysMs);
		return { startDate: start, endDate: end, previousStartDate: prevStart, previousEndDate: prevEnd };
	}, []);

	// Data hooks
	/**
	 * Every `analytics/*` route carries `@UseGuards(AdminAuthGuard)` — read from
	 * `api/src/analytics/analytics.controller.ts` this run — so a stale token or
	 * a revoked admin is refused with no outage at all, on top of the ordinary
	 * 5xx/network paths. This is the operator's HOME screen; before run 97 all
	 * three of those failures printed a number.
	 */
	const { data: revenueData, isLoading: revenueLoading, isError: revenueError } = useGetRevenue(
		selectedLocation?.id, startDate, endDate
	);
	const { data: prevRevenueData } = useGetRevenue(
		selectedLocation?.id, previousStartDate, previousEndDate
	);
	const { data: gameData, isLoading: gameLoading, isError: gameError } = useGetGameCount(
		selectedLocation?.id, startDate, endDate
	);
	const { data: prevGameData } = useGetGameCount(
		selectedLocation?.id, previousStartDate, previousEndDate
	);
	const { data: usageData, isLoading: usageLoading, isError: usageError } = useGetAllCourtsUsage(
		selectedLocation?.id, startDate, endDate
	);
	const { data: prevUsageData } = useGetAllCourtsUsage(
		selectedLocation?.id, previousStartDate, previousEndDate
	);
	const { data: courtsData } = useGetCourts(selectedLocation?.id ?? '');

	// Sparkline data — last 30 days of cumulative bookings/revenue
	const { data: cumulativeBookings } = useGetCumulativeBookings(selectedLocation?.id ?? '', startDate, endDate);
	const { data: cumulativeRevenue } = useGetCumulativeRevenue(selectedLocation?.id ?? '', startDate, endDate);

	const bookingsSparkline = useMemo(() => {
		const raw = isDemo ? getDemoCumulativeBookings(orgId) : cumulativeBookings;
		return (raw ?? []).map((d: any) => d.count ?? d.amount ?? 0);
	}, [isDemo, cumulativeBookings, orgId]);

	const revenueSparkline = useMemo(() => {
		const raw = isDemo ? getDemoCumulativeRevenue(orgId) : cumulativeRevenue;
		return (raw ?? []).map((d: any) => d.amount ?? d.count ?? 0);
	}, [isDemo, cumulativeRevenue, orgId]);

	// Compute values
	/**
	 * Read the three `?? 0` / `?? 1` defaults this block used to carry as the
	 * defect, not as hygiene. `computeGrowth` returns undefined when either
	 * argument is undefined, so substituting a real zero BEFORE the call
	 * disarms that guard — and the result is not a missing figure but a false
	 * one. With the period read refused and the previous-period read answering
	 * (a second request, or a cached one), `computeGrowth(0, 8432)` is exactly
	 * `-100`, so this scoreboard showed the operator `€0` under a red arrow
	 * reading `-100.0% vs previous 30 days`, on their home screen, for a venue
	 * that had taken €8,432. `usage` was worse still: `?? 1` in the
	 * DENOMINATOR makes the ratio 0/1, a well-formed 0% occupancy.
	 *
	 * Every value below therefore stays `undefined` when its read has nothing,
	 * and `unreadable` is `isError && !data` per R96-3 — a failed BACKGROUND
	 * refetch keeps the last good figure rather than blanking a correct screen.
	 */
	const revenueUnreadable = isUnreadable(revenueError, revenueData);
	const gamesUnreadable = isUnreadable(gameError, gameData);
	const usageUnreadable = isUnreadable(usageError, usageData);

	const demoRevenue = isDemo ? getDemoRevenue(startDate, endDate, orgId) : 0;
	const demoPrevRevenue = isDemo ? getDemoRevenue(previousStartDate, previousEndDate, orgId) : 0;
	const revenue = isDemo ? demoRevenue : revenueData?.value;
	const prevRevenue = isDemo ? demoPrevRevenue : prevRevenueData?.value;
	const revenueGrowth = computeGrowth(revenue, prevRevenue);

	const demoGames = isDemo ? getDemoGameCount(startDate, endDate, orgId) : 0;
	const demoPrevGames = isDemo ? getDemoGameCount(previousStartDate, previousEndDate, orgId) : 0;
	const games = isDemo ? demoGames : gameData?.value;
	const prevGames = isDemo ? demoPrevGames : prevGameData?.value;
	const gamesGrowth = computeGrowth(games, prevGames);

	const demoUsage = isDemo ? getDemoCourtsUsageRatio(startDate, endDate, orgId) : 0;
	const demoPrevUsage = isDemo ? getDemoCourtsUsageRatio(previousStartDate, previousEndDate, orgId) : 0;
	const totalGameHours = usageData?.reduce((acc, c) => acc + c.totalGameDurationsInMs, 0);
	const totalAvailHours = usageData?.reduce((acc, c) => acc + c.totalAvailabilityInMs, 0);
	const usage = isDemo
		? demoUsage
		: totalGameHours != null && totalAvailHours ? totalGameHours / totalAvailHours : undefined;
	const prevTotalGameHours = prevUsageData?.reduce((acc, c) => acc + c.totalGameDurationsInMs, 0);
	const prevTotalAvailHours = prevUsageData?.reduce((acc, c) => acc + c.totalAvailabilityInMs, 0);
	const prevUsage = isDemo
		? demoPrevUsage
		: prevTotalGameHours != null && prevTotalAvailHours ? prevTotalGameHours / prevTotalAvailHours : undefined;
	const usageGrowth = computeGrowth(usage, prevUsage);

	const courts = (courtsData ?? []).map((c: any) => ({ id: c.id, name: c.name }));

	const growthLabel = translate('analytics.vs_previous_30_days' as any);
	function growthStr(growth: number | undefined): { text: string; dir: 'up' | 'down' } | undefined {
		if (growth === undefined || growth === null) return undefined;
		const dir = growth >= 0 ? 'up' as const : 'down' as const;
		return { text: `${growth >= 0 ? '+' : ''}${growth.toFixed(1)}% ${growthLabel}`, dir };
	}

	const revGrowth = growthStr(revenueGrowth);
	const gmGrowth = growthStr(gamesGrowth);
	const usGrowth = growthStr(usageGrowth);

	const gap = spacing.sectionGap;

	return (
		<VStack spacing={gap} align="stretch">
			{/* Date heading */}
			<Box>
				<Text
					fontFamily={displayFont}
					fontSize={{ base: '28px', md: '32px' }}
					lineHeight="1.2"
				>
					{formatDateDisplay()}
				</Text>
				{selectedOrganization && (
					<Text fontSize="14px" color={mutedColor} mt="2px">
						{selectedOrganization.name}
						{selectedLocation ? ` — ${courts.length} ${translate('tabs.courts' as any).toLowerCase()}` : ''}
					</Text>
				)}
			</Box>

			{/* Scoreboard Stats */}
			{hasFullContext && (
				<SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
					<ScoreItem
						label={translate('analytics.courts_usage' as any) || 'Occupancy'}
						value={`${((usage ?? 0) * 100).toFixed(0)}`}
						unit="%"
						change={usGrowth?.text}
						changeDirection={usGrowth?.dir}
						isLoading={!hasFullContext || usageLoading}
						isError={!isDemo && usageUnreadable}
					/>
					<ScoreItem
						label={translate('analytics.revenue' as any) || 'Revenue'}
						value={formatCurrency(revenue ?? 0)}
						change={revGrowth?.text}
						changeDirection={revGrowth?.dir}
						isLoading={!hasFullContext || revenueLoading}
						isError={!isDemo && revenueUnreadable}
						sparklineData={revenueSparkline}
					/>
					<ScoreItem
						label={translate('analytics.games' as any) || 'Bookings'}
						value={String(games ?? 0)}
						change={gmGrowth?.text}
						changeDirection={gmGrowth?.dir}
						isLoading={!hasFullContext || gameLoading}
						isError={!isDemo && gamesUnreadable}
						sparklineData={bookingsSparkline}
					/>
				</SimpleGrid>
			)}

			{/* Charts */}
			{hasFullContext && (
				<SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
					<CumulativeBookingsChart />
					<CumulativeRevenueChart />
				</SimpleGrid>
			)}

			{/* Activity Feed */}
			{hasFullContext && (
				<ActivityFeed organizationId={selectedOrganization?.id} />
			)}

			{/* Quick Actions */}
			{hasFullContext && (
				<Box>
					<Text
						color={mutedColor}
						{...typography.formSectionLabel}
						mb={3}
						px={1}
					>
						{translate('walkthrough.quick_actions' as any) || 'Quick Actions'}
					</Text>
					<SimpleGrid columns={{ base: 2, sm: 4, lg: 8 }} gap="12px">
						{QUICK_ACTIONS.map((action) => (
							<NavLink key={action.route} to={action.route}>
								<Card
									py="16px"
									px="12px"
									alignItems="center"
									cursor="pointer"
									transition="all 0.2s cubic-bezier(0.22, 0.61, 0.36, 1)"
									borderColor={actionBorderColor}
									_hover={{
										bg: actionHoverBg,
										transform: 'translateY(-3px)',
										boxShadow: 'md',
									}}
								>
									<Flex
										w="44px"
										h="44px"
										borderRadius="12px"
										bg={action.iconBg}
										align="center"
										justify="center"
										mb="10px"
									>
										<Icon as={action.icon} w="22px" h="22px" color={action.color} />
									</Flex>
									<Text
										{...typography.caption}
										fontWeight="500"
										color={textColor}
										textAlign="center"
										noOfLines={1}
									>
										{translate(action.label as any)}
									</Text>
								</Card>
							</NavLink>
						))}
					</SimpleGrid>
				</Box>
			)}
		</VStack>
	);
}
